import { useEffect, useMemo, useState } from "react";
import { SearchableSelector } from "@/components/searchable-selector";
import { provisioningFeederOptions, substationPickerOptions, tenantPickerOptions } from "@/components/provisioning-picker-options";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, Plus, Trash2, XCircle } from "lucide-react";

import {
  api,
  type Feeder,
  type ResolveAliasResponse,
  type Substation,
  type Tenant,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/toast";
import { formatDeviceId } from "@/lib/format";
import { DEFAULT_NTP_SERVER } from "@/lib/controller-defaults";
import { useAuth } from "@/auth/auth-provider";
import { hasCapability, provisioningTenantId, provisioningVendors } from "@/auth/capabilities";
import { useDialogDraft } from "@/components/dialog-draft";

const DEFAULT_SCRIPT_PATH = "";

type MeterRow = {
  local_id: string;
  modbus_id: string;
  feeder_id: string;
  distribution_company_id: string;
  substation_id: string;
  uses_controller_filters: boolean;
  unassigned: boolean;
  ct_primary: string;
  ct_secondary: string;
  vt_primary: string;
  vt_secondary: string;
};

type AddControllerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type ControllerSetupMode = "provision" | "link";

export function AddControllerDialog({
  open,
  onOpenChange,
}: AddControllerDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { me } = useAuth();
  const canProvision = hasCapability(me, "provisioning:write");
  const canChooseVendor = hasCapability(me, "catalog:write");
  const [setupMode, setSetupMode] = useState<ControllerSetupMode>("provision");
  const [serial, setSerial] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [scriptPath, setScriptPath] = useState(DEFAULT_SCRIPT_PATH);
  const [ntpServer, setNtpServer] = useState(DEFAULT_NTP_SERVER);
  const [defaultDistributionCompanyId, setDefaultDistributionCompanyId] = useState("");
  const [defaultSubstationId, setDefaultSubstationId] = useState("");
  const [resolved, setResolved] = useState<ResolveAliasResponse | null>(null);
  const [resolveError, setResolveError] = useState("");
  const [meters, setMeters] = useState<MeterRow[]>(() => [newMeterRow(1, "", "")]);
  const [reviewing, setReviewing] = useState(false);
  const draft = useDialogDraft();
  const resetDraft = draft.reset;
  useEffect(() => {
    if (!open) resetDraft();
  }, [open, resetDraft]);

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => api.tenants(),
    enabled: open && canProvision && canChooseVendor,
  });
  const distributionCompaniesQuery = useQuery({
    queryKey: ["distribution-companies"],
    queryFn: () => api.distributionCompanies(),
    enabled: open && canProvision,
  });
  const feedersQuery = useQuery({
    queryKey: ["feeders"],
    queryFn: () => api.feeders(),
    enabled: open && canProvision,
  });
  const substationsQuery = useQuery({
    queryKey: ["substations"],
    queryFn: () => api.substations(),
    enabled: open && canProvision,
  });

  const tenants: Tenant[] = useMemo(() => tenantsQuery.data ?? [], [tenantsQuery.data]);
  const vendors = useMemo(
    () => provisioningVendors(me, tenants),
    [me, tenants],
  );
  const feeders: Feeder[] = useMemo(() => feedersQuery.data ?? [], [feedersQuery.data]);
  const substations: Substation[] = useMemo(
    () => substationsQuery.data ?? [],
    [substationsQuery.data],
  );
  const distributionCompanies = useMemo(
    () => distributionCompaniesQuery.data ?? [],
    [distributionCompaniesQuery.data],
  );
  const matchingDefaultFeeders = useMemo(() => {
    return feeders.filter((feeder) =>
      defaultDistributionCompanyId !== "" &&
      defaultSubstationId !== "" &&
      feeder.distribution_company_id === Number(defaultDistributionCompanyId) &&
      feeder.substation_id === Number(defaultSubstationId),
    );
  }, [defaultDistributionCompanyId, defaultSubstationId, feeders]);

  function meterSubstations(meter: MeterRow): Substation[] {
    return substations.filter(
      (substation) => substation.distribution_company_id === Number(meter.distribution_company_id),
    );
  }

  function meterFeeders(meter: MeterRow): Feeder[] {
    return feeders.filter((feeder) =>
      meter.distribution_company_id !== "" &&
      meter.substation_id !== "" &&
      feeder.distribution_company_id === Number(meter.distribution_company_id) &&
      feeder.substation_id === Number(meter.substation_id),
    );
  }

  useEffect(() => {
    if (!open) return;
    const defaultTenant = vendors[0]?.tenant_id;
    if (defaultTenant !== undefined && tenantId === "") {
      setTenantId(String(defaultTenant));
    }
  }, [open, tenantId, vendors]);

  useEffect(() => {
    setResolved(null);
    setResolveError("");
    if (!open || !canProvision || serial.length !== 19) return;
    const handle = window.setTimeout(() => {
      void api
        .resolveAlias(serial)
        .then((result) => {
          setResolved(result);
          setResolveError("");
        })
        .catch((error: unknown) => {
          setResolved(null);
          setResolveError(error instanceof Error ? error.message : "Serial validation failed");
        });
    }, 350);
    return () => window.clearTimeout(handle);
  }, [open, serial, canProvision]);

  const submitMutation = useMutation({
    mutationFn: async () => {
      const selectedTenantId = provisioningTenantId(me, tenants, tenantId);
      if (serial.length !== 19) {
        throw new Error("Enter a complete controller serial");
      }
      const meterRequests = meters.map((meter, index) => {
        const modbusId = parseModbusId(meter.modbus_id);
        const feeder = feeders.find((candidate) => String(candidate.feeder_id) === meter.feeder_id);
        if (!meter.unassigned && !feeder) throw new Error(`Choose a feeder for meter ${index + 1}`);
        const ratios = meter.unassigned ? {
          cp: parseRatio(meter.ct_primary), cs: parseRatio(meter.ct_secondary),
          vp: parseRatio(meter.vt_primary), vs: parseRatio(meter.vt_secondary),
        } : feeder ? {
          cp: feeder.ct_primary, cs: feeder.ct_secondary,
          vp: feeder.vt_primary, vs: feeder.vt_secondary,
        } : null;
        if (!ratios) throw new Error(`Choose a feeder for meter ${index + 1}`);
        return { index, modbusId, feeder, unassigned: meter.unassigned, ratios };
      });
      const seen = new Set<number>();
      for (const meter of meterRequests) {
        if (seen.has(meter.modbusId)) throw new Error(`Meter ID ${meter.modbusId} is duplicated`);
        seen.add(meter.modbusId);
      }

      const meterAssignments = meterRequests.map((meter) => ({
        slot: meter.index + 1,
        modbus_id: meter.modbusId,
        feeder_id: meter.unassigned ? null : meter.feeder?.feeder_id ?? null,
        ...(meter.unassigned ? {
          custom_ct_primary: meter.ratios.cp, custom_ct_secondary: meter.ratios.cs,
          custom_vt_primary: meter.ratios.vp, custom_vt_secondary: meter.ratios.vs,
        } : {}),
      }));
      if (setupMode === "link") {
        return api.linkExistingController({
          tenant_id: selectedTenantId,
          serial_alias: serial,
          meters: meterAssignments,
        });
      }
      const selectedScriptPath = scriptPath.trim();
      const selectedNtpServer = ntpServer.trim();
      if (!selectedNtpServer || selectedNtpServer.length > 128) {
        throw new Error("Enter an NTP server (up to 128 characters)");
      }
      return api.claimDevice({
        tenant_id: selectedTenantId,
        serial_alias: serial,
        script_path: selectedScriptPath,
        ntp_server: selectedNtpServer,
        script_config: {
          m: meterRequests.map((meter) => [
            meter.modbusId,
            {
              ...meter.ratios,
            },
          ]),
        },
        meters: meterAssignments,
      });
    },
    onSuccess: (claim) => {
      void queryClient.invalidateQueries({ queryKey: ["controllers"] });
      void queryClient.invalidateQueries({ queryKey: ["devices"] });
      void queryClient.invalidateQueries({ queryKey: ["feeders"] });
      onOpenChange(false);
      resetForm();
      draft.reset();
      toast({
        title: setupMode === "link" ? "Existing controller linked" : "Controller added",
        description: setupMode === "link"
          ? `${claim.serial_alias} was linked with ${meters.length} feeder assignment${meters.length === 1 ? "" : "s"}. No configuration was sent to the device.`
          : `${claim.serial_alias} is claimed with ${meters.length} meter assignment${meters.length === 1 ? "" : "s"}.`,
        variant: "success",
      });
    },
    onError: (error: unknown) =>
      toast({
        title: "Add controller failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      }),
  });

  function resetForm(): void {
    setSerial("");
    setTenantId("");
    setSetupMode("provision");
    setScriptPath(DEFAULT_SCRIPT_PATH);
    setNtpServer(DEFAULT_NTP_SERVER);
    setDefaultDistributionCompanyId("");
    setDefaultSubstationId("");
    setResolved(null);
    setResolveError("");
    setMeters([newMeterRow(1, "", "")]);
    setReviewing(false);
  }

  function updateMeter(localId: string, patch: Partial<MeterRow>): void {
    setMeters((current) =>
      current.map((meter) => (meter.local_id === localId ? { ...meter, ...patch } : meter)),
    );
  }

  function removeMeter(localId: string): void {
    setMeters((current) => current.filter((meter) => meter.local_id !== localId));
  }

  function updateDefaultDistributionCompany(value: string): void {
    setMeters((current) =>
      current.map((meter) =>
        meter.uses_controller_filters
          ? { ...meter, distribution_company_id: value, substation_id: "", feeder_id: "" }
          : meter,
      ),
    );
    setDefaultDistributionCompanyId(value);
    setDefaultSubstationId("");
  }

  function updateDefaultSubstation(value: string): void {
    setMeters((current) =>
      current.map((meter) =>
        meter.uses_controller_filters
          ? { ...meter, substation_id: value, feeder_id: "" }
          : meter,
      ),
    );
    setDefaultSubstationId(value);
  }

  return (
    <Dialog
      open={open && canProvision}
      onOpenChange={(nextOpen) => {
        if (nextOpen) { draft.reset(); onOpenChange(true); }
        else if (draft.canClose(submitMutation.isPending)) { onOpenChange(false); resetForm(); }
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto sm:max-w-6xl"
        onChangeCapture={(event) => { draft.onChangeCapture(event); setReviewing(false); }}
        onClickCapture={(event) => {
          draft.onClickCapture(event);
          if (event.target instanceof Element && event.target.closest('[role="option"], [role="checkbox"], [role="radio"], [data-draft-change]')) setReviewing(false);
        }}
        onKeyDownCapture={(event) => {
          draft.onKeyDownCapture(event);
          if (event.key === "Enter" && event.target instanceof Element && event.target.hasAttribute("aria-activedescendant")) setReviewing(false);
        }}>
        <DialogHeader>
          <DialogTitle>{setupMode === "link" ? "Link existing controller" : "Add controller"}</DialogTitle>
          <DialogDescription>
            {setupMode === "link"
              ? "Connect an already configured field controller to this cloud organization. Linking saves feeder assignments but does not publish a desired configuration or change the device."
              : "Claim and configure a controller, then assign its Modbus meters to feeders. For an unassigned feeder, enter the CT and VT ratios for the meter."}
          </DialogDescription>
        </DialogHeader>

        <div role="group" aria-label="Controller setup mode" className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant={setupMode === "provision" ? "default" : "outline"}
            aria-pressed={setupMode === "provision"}
            data-draft-change
            onClick={() => setSetupMode("provision")}
          >
            Provision new controller
          </Button>
          <Button
            type="button"
            variant={setupMode === "link" ? "default" : "outline"}
            aria-pressed={setupMode === "link"}
            data-draft-change
            onClick={() => setSetupMode("link")}
          >
            Link existing controller
          </Button>
        </div>
        {setupMode === "link" ? (
          <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
            No desired configuration or MQTT message will be created. The current device report remains separate and unchanged.
            A future configuration change requires an explicit save-and-queue action.
            New telemetry is associated with these feeders from the time of this link; older readings remain unattributed.
          </div>
        ) : null}

        {[tenantsQuery, distributionCompaniesQuery, feedersQuery, substationsQuery].some((query) => query.error) ? (
          <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            Provisioning data did not load. Refresh the page or try again.
          </div>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div className="grid content-start gap-3 sm:grid-cols-2 lg:col-start-2 lg:row-start-1 lg:grid-cols-1">
              <div className="space-y-2">
                <Label htmlFor="controller-serial">Controller serial</Label>
                <Input
                  id="controller-serial"
                  autoCapitalize="characters"
                  placeholder="ABCD-EFGH-IJKL-MNOP"
                  value={serial}
                  onChange={(event) => setSerial(formatControllerSerial(event.target.value))}
                />
                <SerialStatus
                  serial={serial}
                  resolved={resolved}
                  error={resolveError}
                />
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <SearchableSelector label="Vendor" size="form"
                  value={canChooseVendor ? tenantId : String(me?.tenant_id ?? "")}
                  onChange={setTenantId} disabled={!canChooseVendor}
                  placeholder="Select vendor" options={tenantPickerOptions(vendors)} />
              </div>
              {setupMode === "provision" ? (
                <>
              <div className="space-y-2 sm:col-span-2 lg:col-span-1">
                <Label htmlFor="controller-ntp-server">NTP server</Label>
                <Input
                  id="controller-ntp-server"
                  value={ntpServer}
                  maxLength={128}
                  onChange={(event) => setNtpServer(event.target.value)}
                  placeholder={DEFAULT_NTP_SERVER}
                />
                <p className="text-xs text-muted-foreground">
                  Time server included in the controller's initial configuration.
                </p>
              </div>
              <div className="space-y-2 sm:col-span-2 lg:col-span-1">
                <Label htmlFor="controller-script-path">Berry script path</Label>
                <Input
                  id="controller-script-path"
                  value={scriptPath}
                  onChange={(event) => setScriptPath(event.target.value)}
                  placeholder="Empty uses the built-in script"
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to use the built-in script. Use an installed /sd/berry/*.bec
                  file for a custom script.
                </p>
              </div>
                </>
              ) : null}
            </div>

          <div className="space-y-4 lg:col-start-1 lg:row-start-1">
            <div className="space-y-3 rounded-md border border-border bg-muted/20 p-3">
              <div>
                <Label>Controller feeder filters</Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  These set the default filters for all meter rows. A meter can use a different location below.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Distribution company</Label>
                  <SearchableSelector label="Controller distribution company" size="form"
                    value={defaultDistributionCompanyId} onChange={updateDefaultDistributionCompany}
                    placeholder="Select company" options={tenantPickerOptions(distributionCompanies)} />
                </div>
                <div className="space-y-2">
                  <Label>Substation</Label>
                  <SearchableSelector
                    label="Controller substation"
                    size="form"
                    placeholder="Select substation"
                    options={substationPickerOptions(substations.filter(
                      (substation) => substation.distribution_company_id === Number(defaultDistributionCompanyId),
                    ))}
                    value={defaultSubstationId}
                    disabled={defaultDistributionCompanyId === ""}
                    onChange={updateDefaultSubstation}
                  />
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                {matchingDefaultFeeders.length} feeder templates match the controller filters.
              </div>
              {resolved ? (
                <div className="rounded-md border border-secondary/40 bg-secondary/10 p-3 text-xs">
                  <div className="font-semibold text-secondary-foreground">Resolved device</div>
                  <div className="mt-1 font-mono">{formatDeviceId(resolved.device_id)}</div>
                </div>
              ) : null}
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <Label>Meters</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-draft-change
                  onClick={() => setMeters((current) => [
                    ...current,
                    newMeterRow(
                      current.length + 1,
                      defaultDistributionCompanyId,
                      defaultSubstationId,
                    ),
                  ])}
                >
                  <Plus className="h-4 w-4" /> Add meter
                </Button>
              </div>
              {meters.map((meter, index) => (
                <div key={meter.local_id} className="grid min-w-0 gap-3 rounded-md border border-border p-3 sm:grid-cols-2 xl:grid-cols-[100px_minmax(130px,1fr)_minmax(130px,1fr)_minmax(140px,1fr)_auto]">
                  <div className="sm:col-span-2 xl:col-span-5">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" aria-label={`Unassigned feeder for meter ${index + 1}`}
                        checked={meter.unassigned} onChange={(event) => updateMeter(meter.local_id, {
                          unassigned: event.target.checked, feeder_id: "",
                        })} />
                      Unassigned feeder
                    </label>
                    {meter.unassigned ? (
                      <div className="mt-2 grid gap-2 sm:grid-cols-4">
                        <p className="text-xs text-muted-foreground sm:col-span-4">
                          Enter the meter's CT and VT ratios. An unassigned feeder with these values will be reused or created.
                        </p>
                        {(["ct_primary", "ct_secondary", "vt_primary", "vt_secondary"] as const).map((field) => {
                          const label = field.replace("_", " ").toUpperCase();
                          return <label key={field} className="text-xs">{label}
                            <Input type="number" min={1} max={2147483647}
                              aria-label={`${field.startsWith("ct") ? "CT" : "VT"} ${field.endsWith("primary") ? "primary" : "secondary"} for meter ${index + 1}`}
                              value={meter[field]} onChange={(event) => updateMeter(meter.local_id, { [field]: event.target.value })} />
                          </label>;
                        })}
                      </div>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`meter-id-${meter.local_id}`}>Meter ID</Label>
                    <Input
                      id={`meter-id-${meter.local_id}`}
                      type="number"
                      min={1}
                      max={247}
                      value={meter.modbus_id}
                      onChange={(event) => updateMeter(meter.local_id, { modbus_id: event.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Distribution company</Label>
                    <SearchableSelector
                      label={`Distribution company for meter ${index + 1}`}
                      size="form"
                      placeholder="Select company"
                      options={tenantPickerOptions(distributionCompanies)}
                      value={meter.distribution_company_id}
                      onChange={(value) => updateMeter(meter.local_id, {
                        distribution_company_id: value,
                        substation_id: "",
                        feeder_id: "",
                        uses_controller_filters: false,
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Substation</Label>
                    <SearchableSelector
                      label={`Substation for meter ${index + 1}`}
                      size="form"
                      placeholder="Select substation"
                      options={substationPickerOptions(meterSubstations(meter))}
                      value={meter.substation_id}
                      disabled={meter.distribution_company_id === ""}
                      onChange={(value) => updateMeter(meter.local_id, {
                        substation_id: value,
                        feeder_id: "",
                        uses_controller_filters: false,
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Feeder</Label>
                    <SearchableSelector
                      label={`Feeder for meter ${index + 1}`}
                      size="form"
                      value={meter.feeder_id}
                      disabled={meter.unassigned || meter.distribution_company_id === "" || meter.substation_id === ""}
                      onChange={(value) => updateMeter(meter.local_id, { feeder_id: value })}
                      placeholder={meter.substation_id === "" ? "Select company and substation first" : "Select feeder"}
                      options={provisioningFeederOptions(meterFeeders(meter))}
                    />
                  </div>
                  <div className="flex items-end justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove meter ${index + 1}`}
                      data-draft-change
                      disabled={meters.length === 1}
                      onClick={() => removeMeter(meter.local_id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {submitMutation.error ? <p role="alert" className="text-sm text-destructive">
          {submitMutation.error instanceof Error ? submitMutation.error.message : "Could not save controller."}
        </p> : null}
        {reviewing ? <section aria-label="Review controller" className="space-y-1 rounded-md border p-3 text-sm">
          <h3 className="font-semibold">Review controller</h3>
          <p>Serial: {serial || "Not entered"}</p>
          <p>{setupMode === "link" ? "Link saves assignments without sending configuration." : "Provisioning queues the initial desired configuration."}</p>
          {meters.map((meter, index) => {
            const feeder = feeders.find((item) => String(item.feeder_id) === meter.feeder_id);
            const ratio = meter.unassigned
              ? `${meter.ct_primary}:${meter.ct_secondary} CT, ${meter.vt_primary}:${meter.vt_secondary} VT`
              : feeder ? `${feeder.ct_primary}:${feeder.ct_secondary} CT, ${feeder.vt_primary}:${feeder.vt_secondary} VT` : "Unknown ratios";
            return <p key={meter.local_id}>Meter {index + 1}, ID {meter.modbus_id}: {meter.unassigned ? "Unassigned" : feeder?.feeder_name ?? "No feeder selected"} ({ratio})</p>;
          })}
        </section> : null}
        <DialogFooter>
          <Button variant="outline" disabled={submitMutation.isPending} onClick={() => {
            if (draft.canClose(submitMutation.isPending)) { onOpenChange(false); resetForm(); }
          }}>
            Cancel
          </Button>
          <Button disabled={submitMutation.isPending} onClick={() => {
            if (!reviewing) setReviewing(true);
            else submitMutation.mutate();
          }}>
            {submitMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {reviewing ? "Save controller" : "Review controller"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SerialStatus({
  serial,
  resolved,
  error,
}: {
  serial: string;
  resolved: ResolveAliasResponse | null;
  error: string;
}): JSX.Element {
  if (serial.length === 0) {
    return <p className="text-xs text-muted-foreground">Format: ABCD-EFGH-IJKL-MNOP</p>;
  }
  if (serial.length < 19) {
    return <p className="text-xs text-muted-foreground">Serial will validate after 16 characters.</p>;
  }
  if (resolved) {
    return (
      <p className="flex items-center gap-1 text-xs text-secondary">
        <CheckCircle2 className="h-3.5 w-3.5" /> Serial is valid
      </p>
    );
  }
  if (error) {
    return (
      <p className="flex items-center gap-1 text-xs text-destructive">
        <XCircle className="h-3.5 w-3.5" /> {error}
      </p>
    );
  }
  return <p className="text-xs text-muted-foreground">Checking serial...</p>;
}

function newMeterRow(
  position: number,
  distributionCompanyId: string,
  substationId: string,
): MeterRow {
  return {
    local_id: `${Date.now()}-${position}`,
    modbus_id: String(position),
    feeder_id: "",
    distribution_company_id: distributionCompanyId,
    substation_id: substationId,
    uses_controller_filters: true,
    unassigned: false,
    ct_primary: "",
    ct_secondary: "",
    vt_primary: "",
    vt_secondary: "",
  };
}

function formatControllerSerial(input: string): string {
  const cleaned = input
    .toUpperCase()
    .replace(/[ILO]/g, (value) => (value === "O" ? "0" : "1"))
    .replace(/[^0-9A-Z]/g, "")
    .slice(0, 16);
  return cleaned.match(/.{1,4}/g)?.join("-") ?? "";
}

function parseModbusId(value: string): number {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 247) {
    throw new Error("Meter ID must be between 1 and 247");
  }
  return parsed;
}

function parseRatio(value: string): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > 2147483647) {
    throw new Error("Enter positive whole-number CT and VT ratios");
  }
  return parsed;
}
